// externals
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { Dropdown } from '@makemydeal/dr-shared-components';

import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as paymentFeesSelectors from '@makemydeal/dr-dash-store/selectors/paymentFeesSelectors';

import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';

import type { Detail } from './types';
import { useFeesFormContext } from './useFeesFormContext';

import type { StateTree } from '@makemydeal/dr-dash-types';
import { FeeSelectInputContainer } from './FeeCategorySelectInput.style';

import { filterFeesById, getFeeCodes, getFeeCodesForItemizedFees } from './utils';

export type ManualAccessoryProfitFieldProps = {
    feeCategory: paymentServicesTypes.FeeCategory;
    feeIndex: number;
    detail: Detail;
};
const getFeesByCategory = (feeCategory: paymentServicesTypes.FeeCategory) => (state: StateTree) => {
    return paymentFeesSelectors.getFeesByCategory(state, feeCategory);
};

// TODO: Rename component based on SelectInput/ComboBox usage finalized
export const FeeCategorySelectInput: FC<ManualAccessoryProfitFieldProps> = ({ feeCategory, feeIndex, detail }) => {
    const [codeValue, setCodeValue] = useState(detail.code);
    const [feeErrorState, setFeeErrorState] = useState(false);
    const [touched, setTouched] = useState(false);
    const [isBlurred, setIsBlurred] = useState(false);
    const prevCode = useRef<number | undefined>(detail.code);
    const selectContainerRef = useRef<null | HTMLDivElement>(null);
    const {
        getCurrentlySelectedFees,
        handleInputChange,
        setDisableFeeSubmit,
        commitFeeSoftSave,
        handleSoftSaveOnEnterKey,
        handleInputBlur
    } = useFeesFormContext();
    const isLocked = dealLockHooks.useIsDealLocked();
    const feesByCategory = useSelector(getFeesByCategory(feeCategory));
    const codeClassName = 'combo-box';
    const selectClass = `fee-category-${codeClassName}-${feeCategory}-${feeIndex}`;
    const isExistingFee = codeValue !== 0;
    const dispatch = useDispatch();
    const allItemizedFees = useSelector(offerSelectors.getCombinedItemizedFees);
    const appliedFeeCodes = getFeeCodes(allItemizedFees);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isValid = Boolean(codeValue ?? 0);

    // Set use memo to avoid unnecessary re-renders with fees options
    const fees = useMemo(() => {
        const allItemizedFees = getCurrentlySelectedFees().filter((fee) => !fee.isDeleted);
        const currentSelectedFees = getFeeCodesForItemizedFees(allItemizedFees);
        const selectableFees = filterFeesById(feesByCategory, currentSelectedFees);
        return (isExistingFee ? feesByCategory : selectableFees).map((fee) => ({
            label: `${fee.feeDescription} - ${fee.id}`,
            value: fee.id.toString()
        }));
    }, [isExistingFee, feesByCategory]);

    const validateAndCommitChanges = () => {
        if (prevCode.current !== codeValue) {
            prevCode.current = codeValue;
            commitFeeSoftSave();
        }
    };

    const handleBlur = (e: InterstateOnChangeEvent<SelectInputEventValue>) => {
        setTouched(true);
        setTimeout(() => {
            const selectEl = selectContainerRef.current?.querySelector(`[id^="fee-category-${codeClassName}-"]`);
            if (selectEl !== document.activeElement && !selectEl?.contains(document.activeElement)) {
                setIsBlurred(true);
            }
        }, 100);
        if (isDealScreenRedDotErrorsEnabled) {
            handleInputBlur({
                category: detail.category,
                field: 'feeTypeId',
                index: feeIndex,
                isValid: e.isValid
            });
        }
    };

    useEffect(() => {
        if (isBlurred) {
            validateAndCommitChanges();
        }
    }, [isBlurred]);

    const handleChangeCode = (codeValue: string) => {
        if (!codeValue) return;

        setIsBlurred(false);
        const parsedCodeValue = parseInt(codeValue);
        dispatch(offerActionCreators.updatePaymentCallPendingState(parsedCodeValue !== prevCode.current));

        setCodeValue(parsedCodeValue);

        handleInputChange({ category: detail.category, field: 'feeTypeId', index: feeIndex, value: Number(codeValue) });

        const matchingFeeCategory = feesByCategory.find((feeDetails) => feeDetails.id === parseInt(codeValue));

        /* istanbul ignore next */
        const matchingFeeDescription = matchingFeeCategory?.feeDescription || '';
        const isExistingFees = appliedFeeCodes.includes(parsedCodeValue);

        //When trying to add existing fee again, the blur will not be triggered
        // in this case we need to validate and commit changes
        if (isExistingFees) {
            validateAndCommitChanges();
        }

        if (detail.name !== matchingFeeDescription) {
            handleInputChange({ category: detail.category, field: 'feeName', index: feeIndex, value: matchingFeeDescription });
            if (isDealScreenRedDotErrorsEnabled) {
                handleInputBlur({
                    category: detail.category,
                    field: 'feeName',
                    index: feeIndex,
                    isValid: true
                });
            }
        }
    };

    useEffect(() => {
        setFeeErrorState(codeValue === 0);
        setDisableFeeSubmit(codeValue === 0);
    }, [codeValue, setDisableFeeSubmit]);

    return (
        <FeeSelectInputContainer
            className="edit-fee--code-cell-dropdown"
            ref={selectContainerRef}
            onKeyDown={handleSoftSaveOnEnterKey(validateAndCommitChanges)}
        >
            <Dropdown
                minWidthForOptions="190px"
                id={selectClass}
                label={'Code'}
                key={selectClass}
                displayLabel={false}
                disabled={isLocked}
                data-testid={selectClass}
                allowRemoval={false}
                onChange={(e) => handleChangeCode(e.target.value || '')}
                name={selectClass}
                options={fees}
                required={true}
                value={(codeValue || '').toString()}
                errorMessage={!isValid && !touched ? 'Select valid fee code' : 'Code is required'}
                hasError={(touched && feeErrorState) || !isValid}
                onBlur={handleBlur}
                selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                size={'sm'}
            />
        </FeeSelectInputContainer>
    );
};
